// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import QtyField, { QtyFieldStyles } from "@awc/App/Components/Qty/QtyField";
import xUseQty from "@awc/App/Hooks/xUseQty";
import { maxQtyNumber } from "@awc/XDL/Utilities";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { TextFieldProps } from "@insite/mobius/TextField";
import React, { ChangeEvent, useEffect, useState } from "react";
import { connect } from "react-redux";

interface OwnProps extends TextFieldProps {
    configurationCompleted?: boolean;
    configurationSelection?: SafeDictionary<string>;
    variantSelectionCompleted?: boolean;
    labelOverride?: React.ReactNode;
    extendedStyles?: QtyFieldStyles;
    displayTooltip?: boolean;
    // XNG-Changes
    showLabel?: boolean;
    onBlurQtyUpdate?: boolean;
}

const mapStateToProps = (
    state: ApplicationState,
    props: {
        configurationCompleted?: boolean;
        variantSelectionCompleted?: boolean;
        configurationSelection?: SafeDictionary<string>;
    } & HasProductContext,
) => {
    return {
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
            props.configurationSelection,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasProductContext;

export const productQuantityOrderedStyles: QtyFieldStyles = {};

const ProductQuantityOrdered = ({
    hasEnoughInventory,
    productContext: {
        product: { id: productId, detail, minimumOrderQty = 1 },
        onQtyOrderedChanged,
        productInfo: { qtyOrdered, failedToLoadInventory },
    },
    labelOverride,
    extendedStyles,
    canAddToCart,
    configurationCompleted,
    configurationSelection,
    variantSelectionCompleted,
    // XNG-Change
    showLabel = false,
    onBlurQtyUpdate,
    ...otherProps
}: Props) => {
    const [styles] = useState(() => mergeToNew(productQuantityOrderedStyles, extendedStyles));
    const [productQty, setProductQty] = useState(qtyOrdered.toString());
    const [minQtyState, setMinQty] = useState(1);
    const [maxQtyState, setMaxQty] = useState(maxQtyNumber);

    useEffect(() => {
        if (detail?.multipleSaleQty && detail?.multipleSaleQty > 1) {
            const maxQty = maxQtyNumber - (maxQtyNumber % detail.multipleSaleQty);
            setMaxQty(maxQty);
        }
    }, [detail?.multipleSaleQty]);

    // Xng-Changes
    const { debouncedHandleChange, handleChange } = xUseQty({
        minQtyState,
        multipleSaleQty: detail?.multipleSaleQty && Number(detail?.multipleSaleQty),
        onQtyOrderedChanged,
        productId,
        setProductQty,
    });

    useEffect(() => {
        if (qtyOrdered === Number(productQty)) {
            return;
        }
        // Xng-Changes
        debouncedHandleChange(`${Math.max(minimumOrderQty || 1, qtyOrdered) || 1}`);
    }, [qtyOrdered]);

    // Xng-Changes
    useEffect(() => {
        const minQty = Math.max(minimumOrderQty, 1);
        const qty =
            qtyOrdered === 1 && detail?.multipleSaleQty && detail?.multipleSaleQty > 1
                ? Math.max(minQty, detail?.multipleSaleQty)
                : Math.max(minQty, qtyOrdered);
        debouncedHandleChange(qty.toString());
        setMinQty(minQty);
    }, [minimumOrderQty]);

    const keyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.which === 13) {
            (event.target as any).blur();
        }
    };

    // Xng-Changes: fix OOTB issue on Quick Order page, send request on blur or on increase or decrease action
    const qtyChangeHandler = (
        event: ChangeEvent<HTMLInputElement> | string,
        { triggerOnQtyChange = true, force = false } = {},
    ) => {
        const value = typeof event === "string" ? event : event?.target.value;

        setProductQty(value);

        if (force) {
            handleChange(value);
        } else if (triggerOnQtyChange) {
            debouncedHandleChange(value);
        }
    };

    // Xng-Changes
    const blurHandler = (event: ChangeEvent<HTMLInputElement>) => {
        if (onBlurQtyUpdate && event?.target.value) {
            qtyChangeHandler(event, { force: true });
        } else if (Number(productQty) < minQtyState) {
            qtyChangeHandler(`${minQtyState}`);
        }
    };

    // XNG-Change use QtyField
    return (
        <QtyField
            label={labelOverride}
            showLabel={showLabel}
            value={productQty}
            data-test-selector="product_qtyOrdered"
            {...otherProps}
            onKeyPress={keyPressHandler}
            onChange={
                onBlurQtyUpdate
                    ? (e: any, { force }: any) => qtyChangeHandler(e, { triggerOnQtyChange: false, force })
                    : qtyChangeHandler
            }
            onBlur={blurHandler}
            minimumOrderQty={minimumOrderQty}
            multipleSaleQty={detail?.multipleSaleQty}
            extendedStyles={styles}
            min={Math.max(minQtyState, detail?.multipleSaleQty || 1)}
            max={maxQtyState}
            disabled={(!hasEnoughInventory && !failedToLoadInventory) || !canAddToCart || otherProps.disabled}
        />
    );
};

export default withProductContext(connect(mapStateToProps)(ProductQuantityOrdered));
